<template>
  <div class="cover" v-if="!appointment">
    <smd-loading-spinner/>
  </div>
  <smd-one-col-layout class="appointment-details"
                      content-styles="reset-mp"
                      v-else>
    <smd-header class="appointment-details__header"
                slot="sub-toolbar">
      <smd-btn class="back-btn"
               icon="ns-icon-ico028"
               slot="left-actions"
               :bg="false"
               @click="goToAppointmentsList"/>
      <div class="right-action"
           slot="right-actions"
           v-if="showMessageButton">
        <!--          <span class="text">Send</span>-->
        <smd-btn class="button"
                 icon="ns-icon-message"
                 @click="goToSendMessage"/>
      </div>
    </smd-header>

    <!--      DETAILS -->
    <div class="appointment-details__content"
         v-if="appointment">
      <!--      Doctor details -->
      <div class="smd-form__row">
        <div class="image">
          <smd-profile-img :image="profileUrl"
                           :size="`l`"/>
        </div>
        <div class="flex-col align-self-center">
          <h3 class="Title1">{{ appointment.doctorname | doctorNameWithTitle }}</h3>
          <p class="Body3">{{ mainSpecialty }}</p>
        </div>
      </div>
      <!--      Appointment details - now template -->
      <appointment-details-now v-if="isNow"
                               :appointment="this.appointment"/>

      <!--            Appointment details - other statuses -->
      <div class="details"
           v-if="!isNow">
        <div>
                <span class="icon consultation-icon"
                      :class="typeOfConsultClass"/>
        </div>
        <div>
          <div>
            <p class="consult-type"> {{ typeOfConsultText }} </p>
            <p class="consult-date">
              <span v-html="formattedAppointmentDate"></span>
            </p>
          </div>
          <div class="flex-row--baseline-first">
            <p class="patient-name">{{ formattedPatientName }}</p>
          </div>
        </div>
      </div>

      <div class="status-type"
           v-if="!isNow">
        <div>
                <span class="icon status-icon ns-appointment__icon"
                      :class="appointmentStatusIcon"/>
        </div>
        <div>
          <div>
            <p class="Body2">{{ stringSet.title }}</p>
            <p class="Label1 gray-400">{{ stringSet.subtitle }}</p>
            <div v-if="showMessageButton" class="msg-doctor-container">
              <smd-btn type="button"
                       @click="goToSendMessage">
                Message Doctor
              </smd-btn>
            </div>
          </div>
        </div>
      </div>
    </div>
  </smd-one-col-layout>
</template>

<script>
  import moment from 'moment';
  import {mapGetters} from 'vuex';
  import {
    GETTER_LOADED,
    GETTER_NOW_APPOINTMENTS,
    GETTER_FUTURE_APPOINTMENTS,
    GETTER_DENIED_APPOINTMENTS,
    GETTER_PENDING_APPOINTMENTS,
    GETTER_NEW_REQUEST_APPOINTMENTS,
  } from '@/store/types';
  import {
    APPOINTMENTS_ROUTE,
    APPOINTMENT_DETAIL_PARAM,
    CHAT_ROUTE,
    MESSAGE_DOCTOR_PARAM,
    MESSAGE_PATIENT_ID_PARAM,
  } from '@/router/routes';
  import AppointmentDetailsNow from '@/components/AppointmentDetailsNow';
  import appointmentDetailsMixin from '@/mixins/appointment-details';
  import {toPhilMoment} from '@/utils/dates';
  import {APPOINTMENT_DETAIL_TYPE_PROP, APPOINTMENT_TYPES} from '@/constants';
  import {getAppointmentId} from '@/utils/appointments';

  export default {
    name: 'appointment-details',
    components: {
      AppointmentDetailsNow
    },
    mixins: [
      appointmentDetailsMixin,
    ],
    props: {
      // if null, expect now/future (approved) appointment
      [APPOINTMENT_DETAIL_TYPE_PROP]: {type: String, default: null},
    },
    data() {
      return {
        appointment: null,
        stringSet: {},
      }
    },
    computed: {
      ...mapGetters({
        loaded: GETTER_LOADED,
        nowAppointments: GETTER_NOW_APPOINTMENTS,
        futureAppointments: GETTER_FUTURE_APPOINTMENTS,
        deniedAppointments: GETTER_DENIED_APPOINTMENTS,
        pendingAppointments: GETTER_PENDING_APPOINTMENTS,
        newRequestAppointments: GETTER_NEW_REQUEST_APPOINTMENTS,
      }),
      isNow() {
        const appointmentDateMoment = this.appointment.type === APPOINTMENT_TYPES.NEW_REQUEST
          ? toPhilMoment(this.appointment.date)
          : moment(this.appointment.date);
        const isToday = appointmentDateMoment.isSame(moment(), 'day');
        return isToday && this.appointment.status === 'Queued';
      },
      showMessageButton() {
        return !!(this.appointment.doctor && this.appointment.patient);
      },
    },
    watch: {
      loaded: {
        handler(newValue) {
          if (newValue) {
            this.loadAppointment();
          }
        },
        immediate: true,
      },
    },
    methods: {
      goToAppointmentsList() {
        this.$router.push({name: APPOINTMENTS_ROUTE})
      },
      goToSendMessage() {
        this.$router.push({
          name: CHAT_ROUTE,
          params: {
            [MESSAGE_DOCTOR_PARAM]: this.appointment.doctor.usertag,
            [MESSAGE_PATIENT_ID_PARAM]: this.appointment.patient.id,
          },
        });
      },
      loadAppointment() {
        const appointmentType = this[APPOINTMENT_DETAIL_TYPE_PROP];
        const appointmentId = this.$route.params[APPOINTMENT_DETAIL_PARAM];

        switch (appointmentType) {
          case APPOINTMENT_TYPES.PENDING:
            this.appointment = this.pendingAppointments.find((appointment) => {
              return getAppointmentId(appointment) === appointmentId;
            });
            break;
          case APPOINTMENT_TYPES.DENIED:
            this.appointment = this.deniedAppointments.find((appointment) => {
              return getAppointmentId(appointment) === appointmentId;
            });
            break;
          case APPOINTMENT_TYPES.NEW_REQUEST:
            this.appointment = this.newRequestAppointments.find((appointment) => {
              return getAppointmentId(appointment) === appointmentId;
            });
            break;
          default:
            this.appointment = [...this.nowAppointments, ...this.futureAppointments].find((appointment) => {
              return getAppointmentId(appointment) === appointmentId;
            });
            break;
        }

        if (!this.appointment) {
          this.goToAppointmentsList();
        } else {
          this.handleTextPerStatus();
        }
      },
    },
  }
</script>

<style lang="scss">
  .appointment-details {
    .sticky-toolbar {
      padding: space(s) space(m-base) !important;
      margin: 0 !important;
      top: 0 !important;
    }
  }

  .appointment-details__content {
    @include box-shadow($shadow);
    background-color: color(base, background);
    border-radius: 0 0 16px 16px;
    padding: space(m-base);

    .right-action {
      display: flex;
      align-items: center;
      flex-direction: row;
      margin-left: auto;
      color: color(base, tags);

      .text {
        @include space-me($prop: margin, $r: s)
      }

      .button {
        color: color(base, tags);
      }
    }

    .image {
      flex-direction: column;
    }

    .details, .status-type, .requirements {
      @include space-me(m);
      display: inline-flex;
      flex-direction: row !important;
    }

    .details {
      @include responsive() {
        padding-bottom: 0;
        padding-left: space(xxl);
      }
    }

    .status-type {
      @include responsive() {
        padding-top: space(xs);
        padding-left: space(xxl);
      }

      .msg-doctor-container {
        margin-top: 8px;
      }
    }

    .consult-date {
      @include font-style(Body1);

      .date {
        @include font-style(Body2);
      }

      .time {
        text-transform: lowercase;
      }
    }

    .label-small {
      color: color(label, text);
      font-size: 12px;
    }

    .consult-type {
      @include font-style(Body2);
    }

    .patient-name {
      @include space-me($prop: margin, $t: s, $b: s, $r: base);
      padding: space(xs) space(s);
      text-align: center;
      font-size: 13px;
      background-color: color(normal, segment);
      border-radius: 22px;
      flex: unset;
      margin-top: space(m);
    }

    .consultation-prep {
      padding: space(s);
      @include space-me($prop: margin, $t: s);

      &-label {
        @include space-me($prop: margin, $b: s);
        color: color(light, dark-bg);
      }

      &-card {
        display: flex;
        flex-direction: row;
        background-color: color(base, background);
        border-radius: radius(medium);
        padding: space(m);
        @include space-me($prop: margin, $t: m);

        .icon {
          @include space-me($prop: margin, $r: s);
          color: color(light, dark-bg);
        }
      }
    }

    .icon {
      @include space-me($prop: margin, $r: s);
    }

    .consultation-icon {
      color: color(gray, text);
      font-size: 20px;
    }

    .status-icon {
      color: color(purple, tags);
      font-size: 20px;
    }

    .requirements-icon {
      color: color(gray, text);
    }

    .ns-appointment__icon {

      &-cancelled {
        color: color(red, tags);
      }

      &-confirmed {
        color: color(blue, tags);
      }

      &-completed {
        color: color(green, tags);
      }

      &-denied {
        color: color(purple, tags);
      }

      &-pending {
        color: color(yellow, tags);
      }

      &-skipped {
        color: color(base, tags);
      }

    }
  }

  .appointment-details__header {
    [class^='ns-icon-'] {
      &:before {
        color: color(gray-400);
      }
    }
  }
</style>